import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Color, Size } from 'const'
import Alert from 'components/Alert'
import _ from 'lodash'
import { Banner, Button, Checkbox, Logout } from 'components/shard'
import { useAccountHook } from '../../../../hooks'
import { WithdrawConfirmModal } from '../../common/WithdrawConfirmModal'
import serviceAPI from 'utils/axios/modules/service'
import { useLoginCallbackHook } from 'hooks'
import WrapperLink from 'components/shard/WrapperLink'

function Account() {
  const {
    user,
    isIdle,
    isLoading,
    errors,
    setErrors,
    approvalDm,
    execGetUser,
    execUpdateApprovalDm,
    goToUserCreditCardURL,
    goToServiceList,
    goToEditTanakaId,
    goToEditAccount,
  } = useAccountHook()

  useEffect(() => {
    execGetUser()
  }, [execGetUser])

  const headingElement = (
    <Heading>
      <div>
        <span>※NINAL登録クレジットカード変更は</span>
        <HeadingLink as="div" role="button" tabIndex={0} onClick={goToServiceList}>
          コチラ
        </HeadingLink>
      </div>
      <div>
        <span>※ウェブショップ登録クレジットカードの変更は</span>
        <HeadingLink as="div" role="button" tabIndex={0} onClick={goToUserCreditCardURL}>
          コチラ
        </HeadingLink>
      </div>
    </Heading>
  )

  return (
    <Wrapper>
      {headingElement}
      {isIdle || isLoading ? (
        <AccountBlock>
          <Caution>アカウント情報を取得中です...</Caution>
        </AccountBlock>
      ) : (
        <AccountDisplay
          user={user}
          errors={errors}
          setErrors={setErrors}
          approvalDm={approvalDm}
          execUpdateApprovalDm={execUpdateApprovalDm}
          goToEditTanakaId={goToEditTanakaId}
          goToEditAccount={goToEditAccount}
        />
      )}
      <Banner />
      <Logout />
    </Wrapper>
  )
}

function AccountDisplay({
  user,
  errors,
  setErrors,
  approvalDm,
  execUpdateApprovalDm,
  goToEditTanakaId,
  goToEditAccount,
}) {
  const [isWithdrawConfirmModalOpen, setIsWithdrawConfirmModalOpen] = useState(false)
  const { resetStorageFunc } = useLoginCallbackHook()

  /* 退会ができるかどうかの確認 */
  const withdrawConfirm = async () => {
    try {
      const response = await serviceAPI.getCanWithdraw()
      if (response.data) {
        setIsWithdrawConfirmModalOpen(true)
      }
    } catch (error) {
      setErrors([error])
    }
  }

  /* 退会処理 */
  const withdraw = async () => {
    try {
      const response = await serviceAPI.postWithdraw()
      if (response.data) {
        resetStorageFunc()

        window.location =
          process.env.REACT_APP_SSO_LOGOUT_URL +
          encodeURIComponent(window.location.protocol + '//' + window.location.host + '/ninal/mypage/login')
      }
    } catch (error) {
      setErrors([error])
    }
  }

  return (
    <React.Fragment>
      {errors.length > 0 && (
        <Alert type="error" message="エラー" description={errors.join('\n')} onClose={() => setErrors([])} />
      )}
      <AccountBlock>
        {_.isEmpty(user) ? (
          <EmptyParagraph>アカウント情報がありません</EmptyParagraph>
        ) : (
          <React.Fragment>
            <ListLabelArea>
              <TanakaLabel>アカウント情報</TanakaLabel>
              <ListLabelLink>
                <EditLink as="div" onClick={goToEditTanakaId}>
                  編集する
                </EditLink>
              </ListLabelLink>
            </ListLabelArea>
            <TextArea>
              <IdEmailLabel>TANAKA-ID</IdEmailLabel>
              <IdEmail>{user.tanaka_id}</IdEmail>
            </TextArea>
            <ListLabelArea>
              <ListLabel>お客様情報</ListLabel>
              <ListLabelLink>
                <EditLink as="div" onClick={goToEditAccount}>
                  編集する
                </EditLink>
              </ListLabelLink>
            </ListLabelArea>
            <TextArea>
              <NameLabel>氏名</NameLabel>
              <Name>
                {user.lastname} {user.firstname}
                <Kana>
                  {user.lastname_kana} {user.firstname_kana}
                </Kana>
              </Name>
              <Separator />
              <AddressLabel>住所</AddressLabel>
              {!_.isEmpty(user.addresses) && (
                <Address>
                  〒{user.addresses.address1.zipcode}
                  <br />
                  {user.addresses.address1.state}
                  {user.addresses.address1.city}
                  {user.addresses.address1.address1}
                  {user.addresses.address1.address2}
                  {user.addresses.address1.address3}
                </Address>
              )}
              <Separator />
              <TellLabel>連絡先</TellLabel>
              <Tell>
                {user.telephone && <TellContent>電話番号：{user.telephone}</TellContent>}
                {user.mobilephone && <TellContent>携帯電話番号：{user.mobilephone}</TellContent>}
              </Tell>
              <Separator />
              <EmailLabel>メールアドレス</EmailLabel>
              <EmailValue>{user.emailaddress1 || ''}</EmailValue>
            </TextArea>
            <ListLabelArea>
              <ListLabel>お知らせメールの配信とはがきの送付</ListLabel>
            </ListLabelArea>
            <CheckArea>
              <CheckboxArea>
                <Checkbox
                  check={approvalDm === true}
                  onClick={() => {
                    if (approvalDm === false) execUpdateApprovalDm(true)
                  }}
                />
              </CheckboxArea>
              <CheckboxLabel>希望する</CheckboxLabel>
              <CheckboxArea>
                <Checkbox
                  check={approvalDm === false}
                  onClick={() => {
                    if (approvalDm === true) execUpdateApprovalDm(false)
                  }}
                />
              </CheckboxArea>
              <CheckboxLabel>希望しない</CheckboxLabel>
              <Notes>
                ※保書期間のお知らせや、NINAL（ニナル）契約に関するお知らせは配信停止できませんので、あらかじめご了承ください。
                <br />
                ※配信停止には数日かかる場合があり、停止後もメールが配信される可能性がございます。
              </Notes>
            </CheckArea>
          </React.Fragment>
        )}
        <ButtonWrapper>
          <Button fontSize={Size.FONT.BIGGER} onClick={withdrawConfirm}>
            退会する
          </Button>
        </ButtonWrapper>
        <WithdrawalCaution>
          <p>
            ※NINAL契約中の方は、NINAL契約終了までは退会できません。My TANAKA会員サービスについて詳しくは
            <a href="https://www.tanaka-megane.co.jp/mytanaka/" target="_blank" rel="noopener noreferrer">
              コチラ
            </a>
          </p>
        </WithdrawalCaution>
        <WithdrawConfirmModal
          display={isWithdrawConfirmModalOpen}
          yesButtonClick={withdraw}
          noButtonClick={() => setIsWithdrawConfirmModalOpen(false)}
        />
      </AccountBlock>
    </React.Fragment>
  )
}

const Wrapper = styled.div``
const AccountBlock = styled.div`
  padding-bottom: 40px;
  margin-bottom: 60px;
`
const Caution = styled.div`
  margin: 20px;
`
const ListLabelArea = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  padding: 9px 21px 9px 21px;
  background-color: ${Color.BACKGROUND.MOBILE_LABEL};
`
const ListLabelStyle = `
  grid-column: 1;
  font-weight: 700;
`
const ListLabel = styled.div`
  ${ListLabelStyle};
  font-size: ${Size.FONT.BIGGER};
`
const TanakaLabel = styled.div`
  ${ListLabelStyle};
  font-size: ${Size.FONT.LG};
`
const ListLabelLink = styled.div`
  grid-column: 2;
  font-size: ${Size.FONT.REGULAR};
`
const EditLink = styled(WrapperLink)`
  font-size: ${Size.FONT.NORMAL};
  text-decoration: none;
  font-weight: 700;
  cursor: pointer;
`
const TextLabel = `
  font-size: ${Size.FONT.BIG};
  padding: 20px 0 15px 0;
  font-weight: 700;
`
const Text = `
  font-size: ${Size.FONT.BIGGER};
  padding-bottom: 20px;
  font-weight: 500;
`
const TextArea = styled.div`
  padding: 0 20px 4px 20px;
`
const CheckArea = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr;
  padding: 14px 20px 38px 20px;
`
const IdEmailLabel = styled.div`
  ${TextLabel}
`
const IdEmail = styled.div`
  ${Text}
`
const Separator = styled.hr`
  border-top: 1px solid ${Color.LINE.NORMAL};
  border-bottom: 0;
  margin: 0;
`
const NameLabel = styled.div`
  ${TextLabel}
`
const Name = styled.div`
  ${Text}
`
const EmailLabel = styled.div`
  ${TextLabel}
`

const EmailValue = styled.div`
  ${Text}
`
const Kana = styled.span`
  font-size: ${Size.FONT.NORMAL};
  margin-left: 8px;
  font-weight: 500;
`
const AddressLabel = styled.div`
  ${TextLabel}
`
const Address = styled.div`
  ${Text}
`
const TellLabel = styled.div`
  ${TextLabel}
`
const Tell = styled.div`
  ${Text}
`
const TellContent = styled.div`
  margin-top: 10px;
`
const CheckboxArea = styled.div`
  grid-column: 1;
  padding: 14px;
`
const CheckboxLabel = styled.div`
  grid-column: 2;
  font-size: ${Size.FONT.BIGGER};
  padding: 10px 14px;
  font-weight: 700;
`
const Notes = styled.div`
  grid-column: 1 / 3;
  font-size: ${Size.FONT.NORMAL};
  padding-top: 16px;
  font-weight: 500;
`
const ButtonWrapper = styled.div`
  text-align: center;
  margin-top: 16px;
`
const WithdrawalCaution = styled.div`
  ${TextLabel}
  font-weight: 500;
  > p {
    line-height: 1.6rem;
    padding: 0 20px;
    letter-spacing: 0 !important;
    font-size: ${Size.FONT.NORMAL};
    > a {
      text-decoration: underline;
      display: inline-block;
    }
  }
`

const Heading = styled.h5`
  font-weight: 700;
  color: ${Color.FONT.ANNOTATION_RED};
  padding-bottom: 30px;
  font-size: ${Size.FONT.BIG};
  padding: 0 20px;
  padding-bottom: 30px;

  a {
    color: inherit;
    text-decoration: underline;
  }

  span {
    color: inherit;
  }

  div[role='button'] {
    display: inline-block;
    text-decoration: underline;
  }
`

const HeadingLink = styled.a`
  cursor: pointer;
  display: inline-block;
`

const EmptyParagraph = styled.p`
  padding: 0 20px;
`

AccountDisplay.propTypes = {
  user: PropTypes.object,
  errors: PropTypes.array,
  setErrors: PropTypes.func,
}

export default Account
